import React, { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { Search, Filter, Sparkles, MessageCircle, Globe, GraduationCap, Grid, List, ChevronUp, ChevronDown } from 'lucide-react'
import { useWorldSimulatorStore } from '../stores/worldSimulatorStore'
import { usePlayCreateStore } from '../stores/playCreateStore'
import { WorldCard } from '../components/WorldCard'
import { CharacterCardWorkshop } from '../components/CharacterCardWorkshop'
import worldSimulatorStorageService from '../services/worldSimulatorStorageService'
import playCreateStorageService from '../services/playCreateStorageService'
import toast from 'react-hot-toast'
import { useTheme } from '../contexts/ThemeContext'

export const ExplorePage = () => {
  const navigate = useNavigate()
  const { loadWorlds } = useWorldSimulatorStore()
  const { loadCharacters } = usePlayCreateStore()
  const { mainCategory, setMainCategory, theme } = useTheme()
  // 子分类：'all' | 'characters' | 'worlds' | 'beginner'
  const [subCategory, setSubCategory] = useState('all')
  const [isLoading, setIsLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [sortBy, setSortBy] = useState('popular')
  const [selectedTags, setSelectedTags] = useState([])
  const [showTagFilter, setShowTagFilter] = useState(false)
  const [publishedCharacters, setPublishedCharacters] = useState([])
  const [publishedWorlds, setPublishedWorlds] = useState([])
  const [characterStates, setCharacterStates] = useState({})
  // 从 localStorage 读取保存的布局选择，默认为 'grid'
  const [layoutMode, setLayoutMode] = useState(() => {
    const savedLayout = localStorage.getItem('homePageLayoutMode')
    return savedLayout === 'slide' ? 'slide' : 'grid'
  })
  const slideContainerRef = useRef(null)
  const [isDragging, setIsDragging] = useState(false)
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 })
  const [scrollStart, setScrollStart] = useState({ x: 0, y: 0 })
  const [hasDragged, setHasDragged] = useState(false)

  // 根据主分类和子分类定义可用的tag
  const getAvailableTags = () => {
    // 新手区不显示tag筛选，因为新手区内容已经经过筛选
    if (subCategory === 'beginner') {
      return []
    }
    
    const orientationTags = ['男性向', '女性向']
    
    // 角色的tag
    const characterCommonTags = ['治愈', '温柔', '活泼', '高冷', '腹黑', '傲娇', '天然']
    const characterMaleTags = ['霸道', '热血', '中二', '成熟', '温柔', '冷酷']
    const characterFemaleTags = ['甜美', '御姐', '萝莉', '女王', '软萌', '知性']
    
    // 世界的tag
    const worldCommonTags = ['冒险', '悬疑', '浪漫', '日常', '奇幻', '科幻', '历史']
    const worldMaleTags = ['战斗', '策略', '竞技', '成长', '逆袭', '热血', '爽文']
    const worldFemaleTags = ['甜宠', '虐恋', '宫斗', '校园', '职场', '古风', '现代']
    
    // 根据子分类选择基础tag
    if (subCategory === 'characters') {
      // 角色tag
      if (mainCategory === 'male') {
        return Array.from(new Set([...characterCommonTags, ...characterMaleTags, ...orientationTags]))
      } else if (mainCategory === 'female') {
        return Array.from(new Set([...characterCommonTags, ...characterFemaleTags, ...orientationTags]))
      }
      return Array.from(new Set([...characterCommonTags, ...characterMaleTags, ...characterFemaleTags, ...orientationTags]))
    } else if (subCategory === 'worlds') {
      // 世界tag
      if (mainCategory === 'male') {
        return Array.from(new Set([...worldCommonTags, ...worldMaleTags, ...orientationTags]))
      } else if (mainCategory === 'female') {
        return Array.from(new Set([...worldCommonTags, ...worldFemaleTags, ...orientationTags]))
      }
      return Array.from(new Set([...worldCommonTags, ...worldMaleTags, ...worldFemaleTags, ...orientationTags]))
    } else {
      // 全部：合并角色和世界的tag
      const allCharacterTags = mainCategory === 'male' 
        ? [...characterCommonTags, ...characterMaleTags]
        : mainCategory === 'female'
        ? [...characterCommonTags, ...characterFemaleTags]
        : characterCommonTags
      
      const allWorldTags = mainCategory === 'male'
        ? [...worldCommonTags, ...worldMaleTags]
        : mainCategory === 'female'
        ? [...worldCommonTags, ...worldFemaleTags]
        : worldCommonTags
      
      // 去重并合并
      return Array.from(new Set([...allCharacterTags, ...allWorldTags, ...orientationTags]))
    }
  }

  // 加载所有发布的作品
  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true)
        
        const [worldsData, charactersData] = await Promise.all([
          loadWorlds().then(() => worldSimulatorStorageService.getAllWorlds()),
          loadCharacters().then(() => playCreateStorageService.getAllCharacterProfiles())
        ])

        // 筛选已发布的作品
        const publicWorlds = (worldsData || []).filter(w => w.isPublic || w.status === 'published')
        const publicCharacters = (charactersData || []).filter(c => c.isPublic || c.status === 'published')
        
        setPublishedWorlds(publicWorlds)
        setPublishedCharacters(publicCharacters)
        
        // 加载角色状态数据
        if (publicCharacters.length > 0) {
          const states = {}
          await Promise.all(
            publicCharacters.map(async (char) => {
              try {
                const state = await playCreateStorageService.getCharacterState(char.id)
                states[char.id] = state
              } catch (error) {
                states[char.id] = null
              }
            })
          )
          setCharacterStates(states)
        }
      } catch (error) {
        console.error('加载内容失败:', error)
        toast.error('加载内容失败')
      } finally {
        setIsLoading(false)
      }
    }
    loadData()
  }, [loadWorlds, loadCharacters])

  // 筛选和排序
  const filteredAndSortedItems = React.useMemo(() => {
    let items = []

    // 根据子分类获取项目
    if (subCategory === 'all') {
      items = [
        ...publishedWorlds.map(w => ({ type: 'world', data: w })),
        ...publishedCharacters.map(c => ({ type: 'character', data: c }))
      ]
    } else if (subCategory === 'worlds') {
      items = publishedWorlds.map(w => ({ type: 'world', data: w }))
    } else if (subCategory === 'characters') {
      items = publishedCharacters.map(c => ({ type: 'character', data: c }))
    } else if (subCategory === 'beginner') {
      // 新手区：显示标记为"新手推荐"或"官方推荐"的内容
      // 根据主分类（男性向/女性向）显示不同的新手内容
      const beginnerWorlds = publishedWorlds
        .filter(w => {
          const tags = w.tags || []
          const isBeginner = tags.includes('新手推荐') || tags.includes('官方推荐') || w.isOfficial
          if (mainCategory === 'male') {
            return isBeginner && (tags.includes('男性向') || w.orientation === 'male' || tags.some(t => t.includes('男')))
          } else if (mainCategory === 'female') {
            return isBeginner && (tags.includes('女性向') || w.orientation === 'female' || tags.some(t => t.includes('女')))
          }
          return isBeginner
        })
        .map(w => ({ type: 'world', data: w }))
      
      const beginnerCharacters = publishedCharacters
        .filter(c => {
          const tags = c.tags || []
          const isBeginner = tags.includes('新手推荐') || tags.includes('官方推荐') || c.isOfficial
          if (mainCategory === 'male') {
            return isBeginner && (tags.includes('男性向') || c.orientation === 'male' || tags.some(t => t.includes('男')))
          } else if (mainCategory === 'female') {
            return isBeginner && (tags.includes('女性向') || c.orientation === 'female' || tags.some(t => t.includes('女')))
          }
          return isBeginner
        })
        .map(c => ({ type: 'character', data: c }))
      
      items = [...beginnerWorlds, ...beginnerCharacters]
    }

    // 根据主分类筛选（男性向/女性向）
    // 注意：新手区已经在上面根据主分类筛选过了，这里跳过
    if (mainCategory !== 'all' && subCategory !== 'beginner') {
      items = items.filter(item => {
        const tags = item.data.tags || []
        const orientation = item.data.orientation || ''
        if (mainCategory === 'male') {
          return tags.includes('男性向') || orientation === 'male' || tags.some(t => t.includes('男'))
        } else if (mainCategory === 'female') {
          return tags.includes('女性向') || orientation === 'female' || tags.some(t => t.includes('女'))
        }
        return true
      })
    }

    // Tag筛选：选择哪个标签就显示包含该标签的内容（包含逻辑，不是排除）
    if (selectedTags.length > 0) {
      items = items.filter(item => {
        const itemTags = item.data.tags || []
        // 确保itemTags是数组
        if (!Array.isArray(itemTags)) {
          return false
        }
        // 只要内容包含任意一个选中的tag，就显示（包含逻辑）
        return selectedTags.some(selectedTag => itemTags.includes(selectedTag))
      })
    }

    // 搜索筛选
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase()
      items = items.filter(item => {
        const name = item.data.name?.toLowerCase() || ''
        const description = item.data.description?.toLowerCase() || ''
        return name.includes(query) || description.includes(query)
      })
    }

    // 排序
    switch (sortBy) {
      case 'popular':
        items.sort((a, b) => {
          const aCount = a.data.playCount || a.data.totalChatCount || 0
          const bCount = b.data.playCount || b.data.totalChatCount || 0
          return bCount - aCount
        })
        break
      case 'new':
        items.sort((a, b) => {
          const aDate = new Date(a.data.createdAt || 0)
          const bDate = new Date(b.data.createdAt || 0)
          return bDate - aDate
        })
        break
      case 'rating':
        items.sort((a, b) => {
          const aRating = a.data.rating || 0
          const bRating = b.data.rating || 0
          return bRating - aRating
        })
        break
      default:
        break
    }

    return items
  }, [mainCategory, subCategory, publishedWorlds, publishedCharacters, searchQuery, sortBy, selectedTags])

  // 处理布局切换，并保存到 localStorage
  const handleLayoutModeChange = (mode) => {
    setLayoutMode(mode)
    localStorage.setItem('homePageLayoutMode', mode)
  }

  // 处理卡片点击 - 统一跳转到ContentDetail页面
  const handleCardClick = (item) => {
    if (item.type === 'world') {
      // 跳转到统一的内容详情页
      navigate(`/content/world/${item.data.worldId || item.data.id}`)
    } else if (item.type === 'character') {
      // 跳转到统一的内容详情页
      navigate(`/content/character/${item.data.id}`)
    }
  }

  // 滑动模式：鼠标拖动处理
  const handleMouseDown = (e) => {
    if (layoutMode !== 'slide' || !slideContainerRef.current) return
    // 如果点击的是按钮或链接，不启用拖动
    if (e.target.closest('button') || e.target.closest('a')) {
      return
    }
    setIsDragging(true)
    setHasDragged(false)
    setDragStart({ x: e.clientX, y: e.clientY })
    setScrollStart({
      x: slideContainerRef.current.scrollLeft,
      y: slideContainerRef.current.scrollTop
    })
  }


  // 滑动模式：滚轮处理
  const handleWheel = (e) => {
    if (layoutMode !== 'slide' || !slideContainerRef.current) return
    e.preventDefault()
    slideContainerRef.current.scrollTop += e.deltaY
  }

  // 滑动模式：导航按钮
  const scrollSlide = (direction) => {
    if (!slideContainerRef.current) return
    const container = slideContainerRef.current
    const scrollAmount = 400 // 每次滚动的距离
    const targetScroll = direction === 'up' 
      ? container.scrollTop - scrollAmount
      : container.scrollTop + scrollAmount
    
    container.scrollTo({
      top: targetScroll,
      behavior: 'smooth'
    })
  }

  // 添加全局事件监听
  useEffect(() => {
    if (isDragging) {
      const handleMouseMoveGlobal = (e) => {
        if (!slideContainerRef.current) return
        const deltaX = e.clientX - dragStart.x
        const deltaY = e.clientY - dragStart.y
        const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY)
        
        // 如果拖动距离超过5px，认为是拖动操作
        if (distance > 5) {
          setHasDragged(true)
          slideContainerRef.current.scrollLeft = scrollStart.x - deltaX
          slideContainerRef.current.scrollTop = scrollStart.y - deltaY
        }
      }

      const handleMouseUpGlobal = (e) => {
        // 如果发生了拖动，阻止卡片点击事件
        if (hasDragged && e.target.closest('.world-card')) {
          e.preventDefault()
          e.stopPropagation()
        }
        setIsDragging(false)
        setHasDragged(false)
      }

      document.addEventListener('mousemove', handleMouseMoveGlobal)
      document.addEventListener('mouseup', handleMouseUpGlobal)
      return () => {
        document.removeEventListener('mousemove', handleMouseMoveGlobal)
        document.removeEventListener('mouseup', handleMouseUpGlobal)
      }
    }
  }, [isDragging, dragStart, scrollStart, hasDragged])


  // 计算新手区统计
  const beginnerStats = React.useMemo(() => {
    const beginnerWorlds = publishedWorlds.filter(w => {
      const tags = w.tags || []
      return tags.includes('新手推荐') || tags.includes('官方推荐') || w.isOfficial
    })
    const beginnerCharacters = publishedCharacters.filter(c => {
      const tags = c.tags || []
      return tags.includes('新手推荐') || tags.includes('官方推荐') || c.isOfficial
    })
    return {
      worlds: beginnerWorlds.length,
      characters: beginnerCharacters.length,
      total: beginnerWorlds.length + beginnerCharacters.length
    }
  }, [publishedWorlds, publishedCharacters])

  const stats = {
    total: publishedWorlds.length + publishedCharacters.length,
    worlds: publishedWorlds.length,
    characters: publishedCharacters.length,
    beginner: beginnerStats.total
  }

  return (
    <div className={`min-h-screen explore-page ${theme.bg} transition-all duration-500`}>
      {/* 顶部栏 - 网格模式显示完整，滑动模式只显示布局切换 */}
      {layoutMode === 'grid' && (
        <div className={`sticky top-16 z-40 bg-dark-900/95 backdrop-blur-sm border-b ${theme.border} transition-colors duration-500`}>
          <div className="max-w-7xl mx-auto px-4 py-4">
            {/* 主分类选择（男性向/女性向） */}
            <div className="flex items-center gap-4 mb-4">
              <button
                onClick={() => {
                  setMainCategory('all')
                  setSelectedTags([]) // 切换分类时清除tag筛选
                }}
                className={`px-6 py-3 rounded-lg font-medium transition-all ${
                  mainCategory === 'all'
                    ? `bg-gradient-to-r ${theme.primary} text-white shadow-lg`
                    : 'bg-dark-800 text-dark-300 hover:bg-dark-700'
                }`}
              >
                全部
              </button>
              <button
                onClick={() => {
                  setMainCategory('male')
                  setSelectedTags([]) // 切换分类时清除tag筛选
                }}
                className={`px-6 py-3 rounded-lg font-medium transition-all ${
                  mainCategory === 'male'
                    ? `bg-gradient-to-r ${theme.primary} text-white shadow-lg`
                    : 'bg-dark-800 text-dark-300 hover:bg-dark-700'
                }`}
              >
                男性向
              </button>
              <button
                onClick={() => {
                  setMainCategory('female')
                  setSelectedTags([]) // 切换分类时清除tag筛选
                }}
                className={`px-6 py-3 rounded-lg font-medium transition-all ${
                  mainCategory === 'female'
                    ? `bg-gradient-to-r ${theme.primary} text-white shadow-lg`
                    : 'bg-dark-800 text-dark-300 hover:bg-dark-700'
                }`}
              >
                女性向
              </button>
            </div>

            <div className="flex items-center justify-between gap-4">

              {/* 搜索框 */}
              <div className="flex-1 max-w-md">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-dark-400" />
                  <input
                    type="text"
                    placeholder="搜索作品..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 bg-dark-800 border border-dark-600 rounded-lg text-white placeholder-dark-400 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                  />
                </div>
              </div>

              {/* 排序和筛选 */}
              <div className="flex items-center gap-2">
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className={`px-4 py-2 bg-dark-800 border ${theme.border} rounded-lg text-white focus:outline-none focus:border-primary transition-colors`}
                >
                  <option value="popular">热门</option>
                  <option value="new">最新</option>
                  <option value="rating">评分</option>
                </select>

                <button
                  onClick={() => setShowTagFilter(!showTagFilter)}
                  className={`px-4 py-2 rounded-lg border transition-all ${
                    showTagFilter || selectedTags.length > 0
                      ? `bg-gradient-to-r ${theme.primary} border-transparent text-white shadow-lg`
                      : `bg-dark-800 ${theme.border} text-white hover:bg-dark-700`
                  }`}
                >
                  <Filter className="w-5 h-5" />
                  {selectedTags.length > 0 && (
                    <span className="ml-1 text-xs">({selectedTags.length})</span>
                  )}
                </button>

                {/* 布局切换按钮 */}
                  <div className="flex items-center gap-1 bg-dark-800 rounded-lg p-1 border border-dark-600">
                    <button
                      onClick={() => handleLayoutModeChange('grid')}
                      className={`p-2 rounded transition-all ${
                        layoutMode === 'grid'
                          ? `bg-gradient-to-r ${theme.primary} text-white shadow-lg`
                          : 'text-dark-300 hover:text-white hover:bg-dark-700'
                      }`}
                      title="网格布局"
                    >
                      <Grid className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => handleLayoutModeChange('slide')}
                      className={`p-2 rounded transition-all ${
                        layoutMode === 'slide'
                          ? `bg-gradient-to-r ${theme.primary} text-white shadow-lg`
                          : 'text-dark-300 hover:text-white hover:bg-dark-700'
                      }`}
                      title="滑动布局"
                    >
                      <List className="w-5 h-5" />
                    </button>
                  </div>
              </div>
            </div>

            {/* 子分类标签（角色/世界） */}
            <div className="flex items-center gap-2 mt-4">
              <button
                onClick={() => {
                  setSubCategory('all')
                  setSelectedTags([]) // 切换子分类时清除tag筛选
                }}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  subCategory === 'all'
                    ? `bg-gradient-to-r ${theme.primary} text-white shadow-lg`
                    : 'bg-dark-800 text-dark-300 hover:bg-dark-700 hover:text-white'
                }`}
              >
                <Sparkles className="w-4 h-4 inline mr-2" />
                全部 ({stats.total})
              </button>
              <button
                onClick={() => {
                  setSubCategory('beginner')
                  setSelectedTags([]) // 切换子分类时清除tag筛选
                }}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors relative ${
                  subCategory === 'beginner'
                    ? `bg-gradient-to-r ${theme.primary} text-white shadow-lg`
                    : 'bg-dark-800 text-dark-300 hover:bg-dark-700 hover:text-white'
                }`}
              >
                <GraduationCap className="w-4 h-4 inline mr-2" />
                新手区 ({stats.beginner})
                {subCategory === 'beginner' && (
                  <span className="absolute -top-1 -right-1 w-2 h-2 bg-yellow-400 rounded-full animate-pulse"></span>
                )}
              </button>
              <button
                onClick={() => {
                  setSubCategory('characters')
                  setSelectedTags([]) // 切换子分类时清除tag筛选
                }}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  subCategory === 'characters'
                    ? `bg-gradient-to-r ${theme.primary} text-white shadow-lg`
                    : 'bg-dark-800 text-dark-300 hover:bg-dark-700 hover:text-white'
                }`}
              >
                <MessageCircle className="w-4 h-4 inline mr-2" />
                角色 ({stats.characters})
              </button>
              <button
                onClick={() => {
                  setSubCategory('worlds')
                  setSelectedTags([]) // 切换子分类时清除tag筛选
                }}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  subCategory === 'worlds'
                    ? `bg-gradient-to-r ${theme.primary} text-white shadow-lg`
                    : 'bg-dark-800 text-dark-300 hover:bg-dark-700 hover:text-white'
                }`}
              >
                <Globe className="w-4 h-4 inline mr-2" />
                世界 ({stats.worlds})
              </button>
            </div>

            {/* 新手区提示 */}
            {subCategory === 'beginner' && (
              <div className={`mt-4 p-4 ${theme.cardBg} rounded-lg border ${theme.border} backdrop-blur-sm`}>
                <div className="flex items-start gap-3">
                  <GraduationCap className="w-5 h-5 text-yellow-400 mt-0.5" />
                  <div>
                    <h3 className="text-sm font-medium text-white mb-1">新手推荐</h3>
                    <p className="text-xs text-dark-400">
                      {mainCategory === 'male' 
                        ? '这里是官方精选的男性向引导内容，帮助你快速上手和体验平台功能。'
                        : mainCategory === 'female'
                        ? '这里是官方精选的女性向引导内容，帮助你快速上手和体验平台功能。'
                        : '这里是官方精选的引导内容，帮助你快速上手和体验平台功能。'}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* 角色介绍 */}
            {subCategory === 'characters' && (
              <div className={`mt-4 p-4 ${theme.cardBg} rounded-lg border ${theme.border} backdrop-blur-sm`}>
                <div className="flex items-start gap-3">
                  <MessageCircle className="w-5 h-5 text-cyan-400 mt-0.5" />
                  <div>
                    <h3 className="text-sm font-medium text-white mb-1">角色互动</h3>
                    <p className="text-xs text-dark-400">
                      {mainCategory === 'male' 
                        ? '探索丰富的男性向角色，与各种性格的角色进行深度对话和互动，体验不同的故事线。'
                        : mainCategory === 'female'
                        ? '探索丰富的女性向角色，与各种性格的角色进行深度对话和互动，体验不同的故事线。'
                        : '探索丰富的角色内容，与各种性格的角色进行深度对话和互动，体验不同的故事线。'}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* 世界介绍 */}
            {subCategory === 'worlds' && (
              <div className={`mt-4 p-4 ${theme.cardBg} rounded-lg border ${theme.border} backdrop-blur-sm`}>
                <div className="flex items-start gap-3">
                  <Globe className="w-5 h-5 text-emerald-400 mt-0.5" />
                  <div>
                    <h3 className="text-sm font-medium text-white mb-1">世界探索</h3>
                    <p className="text-xs text-dark-400">
                      {mainCategory === 'male' 
                        ? '进入精心构建的男性向世界，体验沉浸式的剧情和丰富的互动内容。'
                        : mainCategory === 'female'
                        ? '进入精心构建的女性向世界，体验沉浸式的剧情和丰富的互动内容。'
                        : '进入精心构建的世界，体验沉浸式的剧情和丰富的互动内容。'}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Tag筛选面板 */}
            {showTagFilter && subCategory !== 'beginner' && (
              <div className={`mt-4 p-4 ${theme.cardBg} rounded-lg border ${theme.border} backdrop-blur-sm`}>
                <div className="flex items-center justify-between mb-3">
                  <label className="text-sm font-medium text-white">标签筛选</label>
                  {selectedTags.length > 0 && (
                    <button
                      onClick={() => setSelectedTags([])}
                      className="text-xs text-dark-400 hover:text-white transition-colors"
                    >
                      清除全部
                    </button>
                  )}
                </div>
                <div className="flex flex-wrap gap-2">
                  {getAvailableTags().map(tag => {
                    const isSelected = selectedTags.includes(tag)
                    return (
                      <button
                        key={tag}
                        onClick={() => {
                          setSelectedTags(prev => 
                            prev.includes(tag) 
                              ? prev.filter(t => t !== tag)
                              : [...prev, tag]
                          )
                        }}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 relative ${
                          isSelected
                            ? `bg-gradient-to-r ${theme.primary} text-white shadow-lg shadow-cyan-500/30 border-2 border-white/30 scale-105`
                            : `bg-dark-700/80 text-dark-300 hover:bg-dark-600 hover:text-white border border-dark-600 hover:border-dark-500`
                        }`}
                      >
                        {isSelected && (
                          <span className="absolute -top-1 -right-1 w-5 h-5 bg-white rounded-full flex items-center justify-center shadow-md">
                            <svg className="w-3 h-3 text-cyan-500" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          </span>
                        )}
                        <span className={isSelected ? 'font-bold' : ''}>{tag}</span>
                      </button>
                    )
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      )}


      {/* 内容区域 */}
      {layoutMode === 'grid' ? (
        // 网格布局
        <div className={`max-w-7xl mx-auto py-8 px-4 ${theme.bgOverlay} transition-colors duration-500`}>
          {isLoading ? (
            <div className="flex items-center justify-center min-h-[400px]">
              <div className="text-dark-400">加载中...</div>
            </div>
          ) : filteredAndSortedItems.length === 0 ? (
            <div className="flex flex-col items-center justify-center min-h-[400px] text-center">
              <Sparkles className="w-16 h-16 text-dark-600 mb-4" />
              <h2 className="text-xl font-semibold text-dark-300 mb-2">
                {searchQuery ? '没有找到匹配的作品' : '还没有作品'}
              </h2>
              <p className="text-dark-400">
                {searchQuery ? '尝试调整搜索条件' : '创建你的第一个作品吧！'}
              </p>
            </div>
          ) : (
            <div className="flex flex-wrap justify-center gap-6 transition-all duration-300">
              {filteredAndSortedItems.map((item, index) => (
                item.type === 'world' ? (
                  <WorldCard
                    key={item.data.worldId || item.data.id}
                    world={item.data}
                    onClick={() => handleCardClick(item)}
                    index={index}
                  />
                ) : (
                  <CharacterCardWorkshop
                    key={item.data.id}
                    character={item.data}
                    characterState={characterStates[item.data.id]}
                    onClick={() => handleCardClick(item)}
                    index={index}
                  />
                )
              ))}
            </div>
          )}
        </div>
      ) : (
        // 滑动布局 - 左侧栏始终显示
        <div className={`py-8 px-4 ${theme.bgOverlay} transition-colors duration-500`}>
          <div className="relative">
            {/* 左侧固定筛选面板 - 使用 fixed 定位，始终显示 */}
            <div className="fixed left-0 top-16 bottom-0 w-80 z-30 overflow-y-auto">
              <div className={`${theme.cardBg} border-r ${theme.border} p-4 backdrop-blur-sm space-y-4 h-full`}>
                {/* 布局切换按钮 - 放在左侧面板顶部 */}
                <div className="pb-4 border-b border-dark-700">
                  <label className="text-sm font-medium text-white mb-2 block">布局模式</label>
                  <div className="flex items-center gap-1 bg-dark-800 rounded-lg p-1 border border-dark-600">
                    <button
                      onClick={() => handleLayoutModeChange('grid')}
                      className={`p-2 rounded transition-all flex-1 ${
                        layoutMode === 'grid'
                          ? `bg-gradient-to-r ${theme.primary} text-white shadow-lg`
                          : 'text-dark-300 hover:text-white hover:bg-dark-700'
                      }`}
                      title="网格布局"
                    >
                      <Grid className="w-4 h-4 mx-auto" />
                    </button>
                    <button
                      onClick={() => handleLayoutModeChange('slide')}
                      className={`p-2 rounded transition-all flex-1 ${
                        layoutMode === 'slide'
                          ? `bg-gradient-to-r ${theme.primary} text-white shadow-lg`
                          : 'text-dark-300 hover:text-white hover:bg-dark-700'
                      }`}
                      title="滑动布局"
                    >
                      <List className="w-4 h-4 mx-auto" />
                    </button>
                  </div>
                </div>

                {/* 主分类选择（男性向/女性向） */}
                <div>
                  <label className="text-sm font-medium text-white mb-2 block">分类</label>
                  <div className="flex flex-col gap-2">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                        setMainCategory('all')
                        setSelectedTags([])
                      }}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-all text-left ${
                        mainCategory === 'all'
                          ? `bg-gradient-to-r ${theme.primary} text-white shadow-lg`
                          : 'bg-dark-800 text-dark-300 hover:bg-dark-700 hover:text-white'
                      }`}
                    >
                      全部
                    </button>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                        setMainCategory('male')
                        setSelectedTags([])
                      }}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-all text-left ${
                        mainCategory === 'male'
                          ? `bg-gradient-to-r ${theme.primary} text-white shadow-lg`
                          : 'bg-dark-800 text-dark-300 hover:bg-dark-700 hover:text-white'
                      }`}
                    >
                      男性向
                    </button>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                        setMainCategory('female')
                        setSelectedTags([])
                      }}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-all text-left ${
                        mainCategory === 'female'
                          ? `bg-gradient-to-r ${theme.primary} text-white shadow-lg`
                          : 'bg-dark-800 text-dark-300 hover:bg-dark-700 hover:text-white'
                      }`}
                    >
                      女性向
                    </button>
                  </div>
                </div>

                {/* 搜索框 */}
                <div>
                  <label className="text-sm font-medium text-white mb-2 block">搜索</label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-dark-400" />
                    <input
                      type="text"
                      placeholder="搜索作品..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 bg-dark-800 border border-dark-600 rounded-lg text-white placeholder-dark-400 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary text-sm"
                    />
                  </div>
                </div>

                {/* 子分类标签（角色/世界） */}
                <div>
                  <label className="text-sm font-medium text-white mb-2 block">类型</label>
                  <div className="flex flex-col gap-2">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                        setSubCategory('all')
                        setSelectedTags([])
                      }}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors text-left ${
                        subCategory === 'all'
                          ? `bg-gradient-to-r ${theme.primary} text-white shadow-lg`
                          : 'bg-dark-800 text-dark-300 hover:bg-dark-700 hover:text-white'
                      }`}
                    >
                      <Sparkles className="w-4 h-4 inline mr-2" />
                      全部 ({stats.total})
                    </button>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                        setSubCategory('beginner')
                        setSelectedTags([])
                      }}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors text-left relative ${
                        subCategory === 'beginner'
                          ? `bg-gradient-to-r ${theme.primary} text-white shadow-lg`
                          : 'bg-dark-800 text-dark-300 hover:bg-dark-700 hover:text-white'
                      }`}
                    >
                      <GraduationCap className="w-4 h-4 inline mr-2" />
                      新手区 ({stats.beginner})
                      {subCategory === 'beginner' && (
                        <span className="absolute -top-1 -right-1 w-2 h-2 bg-yellow-400 rounded-full animate-pulse"></span>
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                        setSubCategory('characters')
                        setSelectedTags([])
                      }}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors text-left ${
                        subCategory === 'characters'
                          ? `bg-gradient-to-r ${theme.primary} text-white shadow-lg`
                          : 'bg-dark-800 text-dark-300 hover:bg-dark-700 hover:text-white'
                      }`}
                    >
                      <MessageCircle className="w-4 h-4 inline mr-2" />
                      角色 ({stats.characters})
                    </button>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                        setSubCategory('worlds')
                        setSelectedTags([])
                      }}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors text-left ${
                        subCategory === 'worlds'
                          ? `bg-gradient-to-r ${theme.primary} text-white shadow-lg`
                          : 'bg-dark-800 text-dark-300 hover:bg-dark-700 hover:text-white'
                      }`}
                    >
                      <Globe className="w-4 h-4 inline mr-2" />
                      世界 ({stats.worlds})
                    </button>
                  </div>
                </div>

                {/* 排序 */}
                <div>
                  <label className="text-sm font-medium text-white mb-2 block">排序</label>
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className={`w-full px-4 py-2 bg-dark-800 border ${theme.border} rounded-lg text-white focus:outline-none focus:border-primary transition-colors text-sm`}
                  >
                    <option value="popular">热门</option>
                    <option value="new">最新</option>
                    <option value="rating">评分</option>
                  </select>
                </div>

                {/* 标签筛选 */}
                {subCategory !== 'beginner' && (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-sm font-medium text-white">标签筛选</label>
                      {selectedTags.length > 0 && (
                        <button
                          onClick={() => setSelectedTags([])}
                          className="text-xs text-dark-400 hover:text-white transition-colors"
                        >
                          清除
                        </button>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-2 max-h-60 overflow-y-auto">
                      {getAvailableTags().map(tag => {
                        const isSelected = selectedTags.includes(tag)
                        return (
                          <button
                            key={tag}
                            type="button"
                            onClick={(e) => {
                              e.preventDefault()
                              e.stopPropagation()
                              setSelectedTags(prev => 
                                prev.includes(tag) 
                                  ? prev.filter(t => t !== tag)
                                  : [...prev, tag]
                              )
                            }}
                            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all duration-200 ${
                              isSelected
                                ? `bg-gradient-to-r ${theme.primary} text-white shadow-lg`
                                : `bg-dark-700/80 text-dark-300 hover:bg-dark-600 hover:text-white border border-dark-600`
                            }`}
                          >
                            {tag}
                          </button>
                        )
                      })}
                    </div>
                  </div>
                )}

                {/* 新手区提示 */}
                {subCategory === 'beginner' && (
                  <div className={`p-3 ${theme.cardBg} rounded-lg border ${theme.border} backdrop-blur-sm`}>
                    <div className="flex items-start gap-2">
                      <GraduationCap className="w-4 h-4 text-yellow-400 mt-0.5 flex-shrink-0" />
                      <div>
                        <h3 className="text-xs font-medium text-white mb-1">新手推荐</h3>
                        <p className="text-xs text-dark-400">
                          {mainCategory === 'male' 
                            ? '这里是官方精选的男性向引导内容，帮助你快速上手和体验平台功能。'
                            : mainCategory === 'female'
                            ? '这里是官方精选的女性向引导内容，帮助你快速上手和体验平台功能。'
                            : '这里是官方精选的引导内容，帮助你快速上手和体验平台功能。'}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* 角色介绍 */}
                {subCategory === 'characters' && (
                  <div className={`p-3 ${theme.cardBg} rounded-lg border ${theme.border} backdrop-blur-sm`}>
                    <div className="flex items-start gap-2">
                      <MessageCircle className="w-4 h-4 text-cyan-400 mt-0.5 flex-shrink-0" />
                      <div>
                        <h3 className="text-xs font-medium text-white mb-1">角色互动</h3>
                        <p className="text-xs text-dark-400">
                          {mainCategory === 'male' 
                            ? '探索丰富的男性向角色，与各种性格的角色进行深度对话和互动，体验不同的故事线。'
                            : mainCategory === 'female'
                            ? '探索丰富的女性向角色，与各种性格的角色进行深度对话和互动，体验不同的故事线。'
                            : '探索丰富的角色内容，与各种性格的角色进行深度对话和互动，体验不同的故事线。'}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* 世界介绍 */}
                {subCategory === 'worlds' && (
                  <div className={`p-3 ${theme.cardBg} rounded-lg border ${theme.border} backdrop-blur-sm`}>
                    <div className="flex items-start gap-2">
                      <Globe className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                      <div>
                        <h3 className="text-xs font-medium text-white mb-1">世界探索</h3>
                        <p className="text-xs text-dark-400">
                          {mainCategory === 'male' 
                            ? '进入精心构建的男性向世界，体验沉浸式的剧情和丰富的互动内容。'
                            : mainCategory === 'female'
                            ? '进入精心构建的女性向世界，体验沉浸式的剧情和丰富的互动内容。'
                            : '进入精心构建的世界，体验沉浸式的剧情和丰富的互动内容。'}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* 导航按钮 - 移到左侧面板底部 */}
                <div className="pt-4 border-t border-dark-700">
                  <div className="flex flex-col gap-2">
                    <button
                      onClick={() => scrollSlide('up')}
                      className={`w-full px-4 py-2 rounded-lg ${theme.cardBg} border ${theme.border} text-white hover:bg-dark-700 transition-all shadow-lg backdrop-blur-sm flex items-center justify-center gap-2`}
                      title="向上滚动"
                    >
                      <ChevronUp className="w-4 h-4" />
                      <span className="text-sm">向上</span>
                    </button>
                    <button
                      onClick={() => scrollSlide('down')}
                      className={`w-full px-4 py-2 rounded-lg ${theme.cardBg} border ${theme.border} text-white hover:bg-dark-700 transition-all shadow-lg backdrop-blur-sm flex items-center justify-center gap-2`}
                      title="向下滚动"
                    >
                      <ChevronDown className="w-4 h-4" />
                      <span className="text-sm">向下</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* 右侧滑动内容区域 - 使用 margin-left 为左侧留出空间 */}
            <div className="ml-80 relative h-[calc(100vh-64px)]">
              {isLoading ? (
                <div className="flex items-center justify-center h-full">
                  <div className="text-dark-400">加载中...</div>
                </div>
              ) : filteredAndSortedItems.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-center px-4">
                  <Sparkles className="w-16 h-16 text-dark-600 mb-4" />
                  <h2 className="text-xl font-semibold text-dark-300 mb-2">
                    {searchQuery ? '没有找到匹配的作品' : '还没有作品'}
                  </h2>
                  <p className="text-dark-400">
                    {searchQuery ? '尝试调整搜索条件' : '创建你的第一个作品吧！'}
                  </p>
                </div>
              ) : (
                /* 可滚动的布局 - 使用flex布局保持420*560尺寸 */
                <div
                  ref={slideContainerRef}
                  onMouseDown={handleMouseDown}
                  onWheel={handleWheel}
                  className={`h-full overflow-y-auto overflow-x-hidden px-4 py-6 ${
                    isDragging ? 'cursor-grabbing select-none' : ''
                  }`}
                  style={{
                    scrollbarWidth: 'thin',
                    scrollbarColor: 'rgba(6, 182, 212, 0.3) transparent'
                  }}
                >
                  <div className="flex flex-wrap justify-center gap-6 transition-all duration-300">
                    {filteredAndSortedItems.map((item, index) => (
                      item.type === 'world' ? (
                        <WorldCard
                          key={item.data.worldId || item.data.id}
                          world={item.data}
                          onClick={() => handleCardClick(item)}
                          index={index}
                        />
                      ) : (
                        <CharacterCardWorkshop
                          key={item.data.id}
                          character={item.data}
                          characterState={characterStates[item.data.id]}
                          onClick={() => handleCardClick(item)}
                          index={index}
                        />
                      )
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

